/*
  TYOHAAR hero slider.

  The hero's own element, replacing Dawn's <slideshow-component>. Dawn's got
  three things wrong for a full-width banner, and none of them could be fixed
  without changing the component the announcement bar also runs on:

  1. TIMER. Autoplay was a setInterval that nothing reset. Click an arrow two
     seconds into a three-second wait and the next auto-advance came one
     second later. Here every slide change — arrow, dot, swipe or autoplay —
     starts the wait again from zero. It is a setTimeout re-armed each time,
     not an interval, so there is no running clock to fall out of step with.

  2. LOOPING. On the last slide, "next" scrolled back to position 0, so the
     banners rewound right-to-left across every slide. Here the track carries
     a copy of the last slide before the first and a copy of the first after
     the last. "Next" on the last banner slides forward onto the copy of the
     first, and once it lands the track jumps — invisibly, the two look the
     same — to the real first. "Previous" on the first does the mirror image.
     The dots follow the slide being shown, so the first dot lights as the
     copy arrives.

  3. PAUSING. Its pause-on-focus only ever applied to the announcement bar.
     Here keyboard focus inside the banner holds the timer, as does a finger
     on the track or a hidden tab, so nothing races ahead in the background.
     Each resumes with the full wait.

  Swipe still comes from the scroll-snap track in CSS; this only listens for
  where it settled.

  Expected markup:
    [data-hero-track]          the scrolling track
    [data-hero-slide]          each real slide (the copies are made here)
    button[name="previous"|"next"]
    [data-hero-dot]            one per real slide, in order
    data-autoplay / data-speed on the track, speed in whole seconds
*/

if (!customElements.get('tyoh-hero-slider')) {
  class TyohHeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-hero-slide]'));
      this.count = this.slides.length;
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');

      this.autoplay = this.track.dataset.autoplay === 'true';
      this.speed = Math.max(1, Number(this.track.dataset.speed) || 5) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.timer = null;
      this.settleTimer = null;
      this.sliding = false;
      this.held = new Set();

      // One banner: nothing to move between, nothing to time.
      if (this.count < 2) return;

      this.addClones();

      // Track positions: 0 is the copy of the last slide, 1..count are the
      // real ones, count + 1 is the copy of the first.
      this.position = 1;
      this.jumpTo(1);
      this.paintDots(1);

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = () => this.handleScroll();
      this.onSettle = () => this.settle();
      this.onResize = () => this.jumpTo(this.position);
      this.onVisibility = () => this.hold('hidden', document.hidden);
      // Keyboard focus only. A mouse click focuses the arrow too, and holding
      // on that would stop autoplay for as long as the button kept focus.
      this.onFocusIn = (event) => {
        if (event.target.matches(':focus-visible')) this.hold('focus', true);
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focus', false);
      };
      this.onTouchStart = () => this.hold('touch', true);
      this.onTouchEnd = () => this.hold('touch', false);

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.dots.forEach((dot, i) => {
        dot.heroGo = () => this.goTo(i + 1);
        dot.addEventListener('click', dot.heroGo);
      });

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('scrollend', this.onSettle);
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);

      this.bindEditor();
      this.restart();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      if (this.count < 2 || !this.track) return;

      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.removeEventListener('click', dot.heroGo));
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('scrollend', this.onSettle);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('touchcancel', this.onTouchEnd);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.resizeObserver?.disconnect();
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    /* ------------------------------------------------------------ track */

    addClones() {
      const copy = (slide) => {
        const clone = slide.cloneNode(true);
        // Not a real slide: no id to collide with, nothing for the theme
        // editor to select, nothing for a screen reader or a tab key to find.
        clone.removeAttribute('id');
        clone.removeAttribute('data-hero-slide');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.dataset.heroClone = '';
        clone.querySelectorAll('img').forEach((img) => {
          img.removeAttribute('fetchpriority');
          img.setAttribute('loading', 'lazy');
        });
        return clone;
      };

      this.track.prepend(copy(this.slides[this.count - 1]));
      this.track.append(copy(this.slides[0]));
    }

    width() {
      return this.track.clientWidth || 1;
    }

    // Instant, so the swap from a copy to its real slide is never seen.
    jumpTo(position) {
      this.position = position;
      this.track.scrollTo({ left: position * this.width(), behavior: 'instant' });
    }

    slideTo(position) {
      this.position = position;
      /*
        A SLIDE WE STARTED IS NOT A SWIPE, AND THE DOTS MUST TELL THEM APART.

        handleScroll repaints the dots from the live scroll position so a
        finger drag is followed as it happens. A smooth scrollTo fires the
        same scroll events — and for the first half of the travel
        Math.round(scrollLeft / width) still rounds to the slide we are
        LEAVING. So every arrow click painted the destination dot, then the
        origin dot, then the destination again.

        On the last slide that reads as 5 - 1 - 5 - 1, which looks like the
        dots jumping backwards while the banner moves forwards. The slides
        were always right; the dots were arguing with the animation.

        While this flag is up the dots are left alone, because we already know
        where they belong — we just set them. settle() lowers it, so a real
        swipe goes back to being followed live.
      */
      this.sliding = true;
      this.track.scrollTo({
        left: position * this.width(),
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
      });
      this.paintDots(position);
      // Some browsers do not fire scrollend; settle anyway once it has had
      // time to arrive.
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 700);
    }

    // The real slide (1..count) a track position shows.
    realIndex(position) {
      if (position <= 0) return this.count;
      if (position > this.count) return 1;
      return position;
    }

    /* --------------------------------------------------------- movement */

    step(direction) {
      // Clicked again while still on a copy: move to its real twin first, so
      // the next step has a slide to go to rather than running off the end.
      if (this.position <= 0 || this.position > this.count) {
        this.jumpTo(this.realIndex(this.position));
      }
      this.slideTo(this.position + direction);
      this.restart();
    }

    goTo(index) {
      if (index === this.realIndex(this.position) && this.position >= 1 && this.position <= this.count) {
        this.restart();
        return;
      }
      if (this.position <= 0 || this.position > this.count) {
        this.jumpTo(this.realIndex(this.position));
      }
      this.slideTo(index);
      this.restart();
    }

    handleScroll() {
      // Dots follow a SWIPE as it happens, not only once it lands. A slide we
      // started ourselves already painted them; see slideTo.
      if (!this.sliding) {
        const live = Math.round(this.track.scrollLeft / this.width());
        this.paintDots(live);
      }

      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 160);
    }

    settle() {
      clearTimeout(this.settleTimer);
      // Whatever was moving has stopped, so the dots go back to following the
      // track. Lowered before anything else here, because the clone swap
      // below scrolls again and that one SHOULD be followed.
      this.sliding = false;
      const landed = Math.round(this.track.scrollLeft / this.width());
      const swiped = landed !== this.position;
      this.position = landed;

      // Landed on a copy: swap to the real slide it shows.
      if (landed <= 0 || landed > this.count) {
        this.jumpTo(this.realIndex(landed));
      }
      this.paintDots(this.position);

      // A swipe is a manual change like any other, so the wait starts over.
      if (swiped) this.restart();
    }

    paintDots(position) {
      const active = this.realIndex(position) - 1;
      this.dots.forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle('slider-counter__link--active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* ------------------------------------------------------------ timer */

    // Every change of slide calls this, so the full wait always runs from the
    // moment the slide changed — never from some earlier tick.
    restart() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.reducedMotion.matches || this.held.size > 0) return;
      this.timer = setTimeout(() => this.step(1), this.speed);
    }

    // Reasons to hold the timer. Several can apply at once; it resumes only
    // when all of them have cleared, and resumes with the full wait.
    hold(reason, on) {
      if (on) {
        this.held.add(reason);
        clearTimeout(this.timer);
      } else if (this.held.delete(reason)) {
        this.restart();
      }
    }

    /* ----------------------------------------------------- theme editor */

    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      this.onBlockSelect = (event) => {
        const index = this.slides.indexOf(event.target);
        if (index === -1) return;
        this.hold('editor', true);
        this.jumpTo(index + 1);
        this.paintDots(index + 1);
      };
      this.onBlockDeselect = (event) => {
        if (this.slides.includes(event.target)) this.hold('editor', false);
      };

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  customElements.define('tyoh-hero-slider', TyohHeroSlider);
}
